/**
 * "Are you sure?", once, for the whole app.
 *
 * NOT `window.confirm`. Three reasons, and the third is the one that made this a
 * component rather than one line: a native dialog cannot be translated, it is
 * styled by the operating system in a way that has nothing to do with this app,
 * and on iOS an installed PWA renders it with the *website's origin* in the
 * heading — which on a shared tablet reads as the browser asking, not the app.
 *
 * A native `<dialog>` underneath, though, because `showModal()` is the only thing
 * that gives focus trapping, Escape-to-dismiss, the top layer and an inert
 * background for free. Every hand-rolled overlay in the world gets at least one
 * of those four wrong.
 *
 * THE CALL SHAPE IS DELIBERATELY NOT A PROMISE.
 *
 *     confirm({ title, body, confirmLabel, onConfirm: () => deleteThing() })
 *
 * `await confirm(...)` reads better in isolation and is worse here: every call
 * site is a click handler, an `async` click handler returns a promise React does
 * nothing with, and the lint rule that catches that (`no-misused-promises`) would
 * be silenced at fifteen call sites for the sake of one keyword. A callback also
 * keeps the destructive write in the same shape as everywhere else in this app —
 * fired, never awaited. See api/mutations.ts.
 *
 * The provider sits above the router in main.tsx, so any screen can ask.
 */
import { createContext, type ReactNode, use, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui.tsx';
import { cn } from '@/lib/cn.ts';

export type ConfirmRequest = {
  title: string;
  /** One sentence saying what will be lost. Optional — some questions are the title. */
  body?: string;
  /** The affirmative button. Says what will happen ("Delete"), never "OK". */
  confirmLabel: string;
  /** Defaults to the app's Cancel. Given where the alternative has a name of its own. */
  cancelLabel?: string;
  /**
   * Run when the question is answered "no" — the Cancel button, Escape, the
   * backdrop, the back gesture. Only needed where "no" is itself an action
   * rather than the absence of one: the weighing wizard blocks a navigation
   * while it asks, and something has to let that navigation go again.
   */
  onCancel?: () => void;
  /**
   * `danger` paints the affirmative button as the alert colour and is the default
   * for a deletion. `neutral` is for a question that is merely a fork — "save
   * this weight anyway?" — where a red button would overstate it.
   */
  tone?: 'danger' | 'neutral';
  onConfirm: () => void;
};

const ConfirmContext = createContext<((request: ConfirmRequest) => void) | null>(null);

export const ConfirmProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);

  /**
   * The dialog is opened and closed through the DOM method rather than the `open`
   * attribute, because only `showModal()` puts it in the top layer with a
   * backdrop and an inert page behind it. `open={true}` renders a dialog that
   * scrolls with the document and traps nothing.
   */
  useEffect(() => {
    const element = dialog.current;
    if (element === null) return;
    if (request !== null && !element.open) element.showModal();
    if (request === null && element.open) element.close();
  }, [request]);

  const close = () => setRequest(null);

  /**
   * Every "no" path, and the only one that runs `onCancel`. Separate from
   * `close` because the affirmative button closes the dialog too, and a
   * confirmed question that also reported itself cancelled would let the
   * blocked navigation through twice.
   */
  const dismiss = () => {
    close();
    request?.onCancel?.();
  };

  return (
    <ConfirmContext value={setRequest}>
      {children}

      <dialog
        ref={dialog}
        // Escape and the backdrop both mean "no". `onCancel` is the browser's own
        // event for the Escape key; without clearing our state there the dialog
        // would be closed while React still thought it was open, and the next
        // question would never appear.
        onCancel={(event) => {
          event.preventDefault();
          dismiss();
        }}
        onClick={(event) => {
          // A click on the backdrop lands on the dialog element itself; a click
          // on anything inside it lands on a child. This is the whole test.
          if (event.target === dialog.current) dismiss();
        }}
        aria-labelledby="confirm-title"
        className={cn(
          // Bottom sheet on a phone, where the thumb is, and a centred card from
          // `sm:` up. `m-0 mt-auto` beats the user-agent's own centring, which
          // cannot be overridden with `items-end` from outside.
          'm-0 mt-auto w-full max-w-lg rounded-t-2xl border border-line bg-surface p-5',
          'sm:m-auto sm:rounded-2xl',
          'backdrop:bg-ink/40 backdrop:backdrop-blur-sm',
          '[padding-bottom:calc(env(safe-area-inset-bottom)+1.25rem)] sm:[padding-bottom:1.25rem]',
        )}
      >
        {request !== null && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <h2 id="confirm-title" className="text-lg font-bold tracking-[-0.01em]">
                {request.title}
              </h2>
              {request.body !== undefined && (
                <p className="text-sm text-ink-soft">{request.body}</p>
              )}
            </div>

            {/*
              The affirmative action first on a phone — it is under the thumb and
              it is what the reader came here to do — but the CANCEL is the wider,
              calmer one, and Escape, the backdrop and the back gesture all reach
              it too. A destructive button that is the only easy target is how
              people delete puppies by accident.
            */}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="secondary" className="sm:px-5" onClick={dismiss}>
                {request.cancelLabel ?? t('common.cancel')}
              </Button>
              <Button
                autoFocus
                className={cn(
                  'sm:px-5',
                  (request.tone ?? 'danger') === 'danger' &&
                    'bg-alert hover:bg-alert/90 active:bg-alert/80 disabled:bg-alert/50',
                )}
                onClick={() => {
                  // Closed first, so a confirmed deletion that navigates away
                  // cannot leave a modal open over the next screen.
                  close();
                  request.onConfirm();
                }}
              >
                {request.confirmLabel}
              </Button>
            </div>
          </div>
        )}
      </dialog>
    </ConfirmContext>
  );
};

/**
 * Ask before doing something that cannot be undone.
 *
 * Every deletion in this app goes through this. A destructive write with no
 * question in front of it is a bug, however small the thing being deleted looks
 * from the code — a photograph is somebody's only picture of a puppy that has
 * since been placed.
 */
export const useConfirm = (): ((request: ConfirmRequest) => void) => {
  const ask = use(ConfirmContext);
  if (ask === null) throw new Error('useConfirm outside ConfirmProvider');
  return ask;
};
